import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export interface HeartBurstProps {
  /** When this flips false → true, the burst animation plays once. */
  active: boolean;
  /** Wrapped content — the actual heart icon (or any "love target"). The
   * burst animation rides the wrapper, so children stay agnostic of it. */
  children: ReactNode;
  className?: ClassName;
}

/** Per-particle trajectory for the confetti burst. The 4 particles emit
 *  outward in cardinal-ish directions so the cluster reads as a small
 *  spark rather than a directed beam. */
interface ParticleSpec {
  dx: number;
  dy: number;
  /** Tailwind color class fragment (after `bg-`) for this particle. */
  color: string;
  /** Stagger in ms — slight offsets prevent the particles from looking
   *  like a single ring snapping outward. */
  delay: number;
}

const particles: Array<ParticleSpec> = [
  { dx: -22, dy: -18, color: 'primary-emphasized', delay: 0 },
  { dx: 24, dy: -16, color: 'secondary-emphasized', delay: 40 },
  { dx: -18, dy: 16, color: 'tertiary-emphasized', delay: 70 },
  { dx: 20, dy: 20, color: 'quaternary-emphasized', delay: 30 },
];

/**
 * Plays a kawaii pulse + 4-particle confetti burst on the wrapped icon
 * whenever `active` transitions false → true. Used for the "scout" /
 * "favorite" affordance — the brand's emotional centerpiece.
 *
 * Respects `prefers-reduced-motion`: under that setting the animations
 * are suppressed via the `motion-safe:` Tailwind variant, but the
 * wrapper still mounts (no layout shift).
 */
export const HeartBurst = ({ active, children, className }: HeartBurstProps) => {
  // `burstKey` re-mounts the animated children each time it changes, which
  // restarts the CSS animation cleanly (CSS animations don't replay on
  // class re-application alone).
  const [burstKey, setBurstKey] = useState(0);
  const prevActiveRef = useRef(active);

  useEffect(() => {
    // Only burst on false → true transitions, not on the first mount.
    if (active && !prevActiveRef.current) {
      setBurstKey((k) => k + 1);
    }
    prevActiveRef.current = active;
  }, [active]);

  return (
    <span className={twx('relative inline-flex items-center justify-center', className)}>
      <span
        key={`heart-${burstKey}`}
        // Pulse runs on the wrapper of children so the underlying icon is
        // agnostic of the animation. `key` change above remounts this span
        // so the animation restarts on each burst.
        className={twx('inline-flex', burstKey > 0 && 'motion-safe:animate-kawaii-heart-pulse')}
      >
        {children}
      </span>
      {/* Particles render once per burstKey. They're absolutely positioned
          over the icon center and fly outward via the kawaii-confetti
          keyframe. Hidden when burstKey is 0 to skip the initial mount. */}
      {burstKey > 0 && (
        <span
          key={`particles-${burstKey}`}
          aria-hidden
          className="pointer-events-none absolute inset-0 motion-reduce:hidden"
        >
          {particles.map((p) => (
            <span
              // Particles are static fixtures (color + trajectory), so the
              // color/dx/dy tuple is a stable identifier across renders.
              key={`${p.color}-${p.dx}-${p.dy}`}
              className={twx(
                'absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full motion-safe:animate-kawaii-confetti',
                `bg-${p.color}`,
              )}
              style={
                {
                  '--kawaii-tx': `${p.dx}px`,
                  '--kawaii-ty': `${p.dy}px`,
                  animationDelay: `${p.delay}ms`,
                } as CSSProperties
              }
            />
          ))}
        </span>
      )}
    </span>
  );
};
